import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { getAuthToken, isLoggedIn } from "services/AccountServices";
import { getUpToChar } from "utility/Utils";
import { navigate } from "utility/NavigationService";

// ---=== Private ===--- //

const headers = {
    Accept: "application/json; version=1.0",
    "Content-Type": "application/json"
};

const endpointRequiresLogin = (url: string): boolean => {
    let endpoint = getUpToChar(url.replace(/^\//, ""), "/");
    switch (endpoint) {
        case "diagnostics":
        case "login":
        case "logout":
        case "password":
        case "verify-email":
            return false;
        default:
            return true;
    }
};

// ---=== Axios Instance ===--- //

const axiosInstance: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_KARUNA_CLOUD_URL,
    responseType: "json",
    headers: headers,
    withCredentials: false
});

// ---=== Axios Interceptors ===--- //

axiosInstance.interceptors.request.clear();
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // let accessPath = config.accessPath ? config.accessPath : '';
        // console.log(`${accessPath} => FULFILLED: ${config.url}`);

        if (isLoggedIn()) {
            config.headers["Authorization"] = `Token ${getAuthToken()}`;
        }
        else {
            if (endpointRequiresLogin(config.url)) {
                let controller = new AbortController();
                controller.abort(`KarunaCLOUD accessed while not logged in!`);
                return {
                    ...config,
                    signal: controller.signal
                };
            }

            delete config.headers["Authorization"];
        }

        return config;
    },
    (error) => {
        if (!error.config.url.includes("diagnostics")) {
            let accessPath = error.config.accessPath ? error.config.accessPath : "";
            let errorMsg = `${accessPath} <= REJECTED: ${error.config.url} ${error.config.signal ? error.config.signal.reason : error}`;
            console.error(errorMsg, error);
        }

        return error;
    }
);

axiosInstance.interceptors.response.clear();
axiosInstance.interceptors.response.use(
    (response) => {
        const config = response.config as InternalAxiosRequestConfig;
        let accessPath: string = config.accessPath ? config.accessPath : "";
        console.log(`${accessPath} <= FULFILLED: ${response.config.method} ${response.config.url}`);
        return response;
    },
    (error) => {
        let config = error?.config as InternalAxiosRequestConfig;
        let method = config?.method;
        let requestUrl = config?.url;
        let response = error?.response;

        if (!requestUrl.includes("diagnostics")) {
            let accessPath = config?.accessPath ?? "";
            let errorMsg = `${accessPath} <= REJECTED: ${method ? `${method} `: ""}${requestUrl ?? ""} ${error}`;

            // Our custom override of console.error will dispatch a /diagnostics event
            // to KarunaCLOUD. If no response was received from KarunaCLOUD, there is no
            // reason to dispatch this event as it will not be able to reach KarunaCLOUD.
            // @todo STILL WANT TO print this error to the console without dispatching /diagnostics event
            // if (response) {
            //     console.error(errorMsg, error);
            // }
            // else {
            //     if (isLoggedIn()) {
            //         void API.api_Logout(accessPath, `No Response From KarunaCLOUD`, null);
            //     }
            //     return;
            // }
            //
            // switch (response.status) {
            //     case 401:
            //         if (isLoggedIn()) {
            //             void API.api_Logout(accessPath, `401 Response From KarunaCLOUD: ${error.response.headers}`, null);
            //         }
            //         break;
            //     case 503:
            //         navigate('/maintenance');
            //         break;
            // }

            if (response) {
                // Custom override of console.error will dispatch a /diagnostics event
                console.error(errorMsg, error);

                switch (response.status) {
                    // Only enable if we want to explicitly log users out on any 401 (Unauthorized) error.
                    // Explicit logouts will not allow the user to perform any sort of corrective action.
                    //
                    // case 401:
                    //     if (isLoggedIn()) {
                    //         void API.api_Logout(accessPath, `Unauthorized 401 response From KarunaCLOUD: ${error.response.headers}`, null);
                    //     }
                    //     break;
                    case 503:
                        void navigate("/maintenance");
                        break;
                }
            }
        }

        return response ? response : null;
    }
);

export default axiosInstance;
